import React from "react";
import { Zap, Building2, Code2, Store } from "lucide-react";

const ENERGY_IMG = "https://images.pexels.com/photos/11843858/pexels-photo-11843858.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const INFRA_IMG = "https://images.pexels.com/photos/417273/pexels-photo-417273.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const SOFTWARE_IMG = "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const RETAIL_IMG = "https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const SECTORS = [
  {
    id: "energy",
    icon: Zap,
    name: "Energy",
    eyebrow: "01",
    tagline: "Solar, wind, storage, compressed biogas and the grid behind them.",
    body:
      "We take energy projects from feedstock and PPA to commissioning — siting, interconnection, technology selection, EPC governance and long-term operations — across utility-scale renewables, C&I captives and compressed biogas (CBG) plants that turn agri-residue and organic waste into dispatchable fuel.",
    chips: [
      "Utility-scale solar",
      "Onshore wind",
      "Battery storage & firming",
      "Compressed Biogas (CBG)",
      "C&I / behind-the-meter",
      "Grid interconnection",
    ],
    image: ENERGY_IMG,
  },
  {
    id: "infrastructure",
    icon: Building2,
    name: "Infrastructure",
    eyebrow: "02",
    tagline: "Hard assets built, commissioned and operated to spec.",
    body:
      "We design, capitalise and execute infrastructure programmes — from utilities and logistics corridors to industrial parks — where delivery discipline and long-dated capital have to move in the same direction.",
    chips: ["Utilities", "Logistics", "Industrial parks", "Asset operations"],
    image: INFRA_IMG,
  },
  {
    id: "software-implementation",
    icon: Code2,
    name: "Software Implementation",
    eyebrow: "03",
    tagline: "Platforms, integration and operations-grade software.",
    body:
      "We design and implement the software layer that industrial and energy businesses actually run on — ERP, asset-management, SCADA/IoT integration, analytics and workflow platforms — with an execution focus, not a slide pack.",
    chips: ["ERP & asset mgmt", "SCADA / IoT", "Data & analytics", "Workflow platforms"],
    image: SOFTWARE_IMG,
  },
  {
    id: "retail",
    icon: Store,
    name: "Retail",
    eyebrow: "04",
    tagline: "Distribution, stores and consumer networks at scale.",
    body:
      "We partner with retail and distribution operators on network design, store-economics, digital-physical integration and the transformation programmes that make modern consumer businesses run.",
    chips: ["Network design", "Store economics", "Digital-physical", "Operating systems"],
    image: RETAIL_IMG,
  },
];

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
          Industries
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium max-w-5xl">
          Four sectors, <br /> chosen <em className="not-italic text-[#0F7A3E]">on purpose</em>.
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-[#575B57] leading-relaxed">
          We go deep where capital, infrastructure, software and consumer
          complexity converge — and stay long enough to move the metrics that
          matter.
        </p>
      </section>

      <section className="border-t border-[#E4E5DF]">
        {SECTORS.map((s, idx) => {
          const Icon = s.icon;
          const reversed = idx % 2 === 1;
          return (
            <div
              key={s.id}
              id={s.id}
              data-testid={`industry-row-${s.id}`}
              className={`border-b border-[#E4E5DF] ${idx % 2 === 0 ? "bg-[#FBFBF9]" : "bg-[#F3F3E8]/60"}`}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                <div className={`md:col-span-5 ${reversed ? "md:order-2" : ""}`}>
                  <div className="relative overflow-hidden aspect-[4/5] w-full">
                    <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover saafi-img-reveal" />
                  </div>
                </div>
                <div className={`md:col-span-6 ${reversed ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
                  <Icon className="w-8 h-8 text-[#0F7A3E] mb-8" strokeWidth={1.25} />
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
                    {s.eyebrow} — Sector
                  </p>
                  <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight font-medium">
                    {s.name}
                  </h2>
                  <p className="mt-6 text-lg leading-relaxed text-[#575B57]">{s.body}</p>
                  <div className="mt-10 flex flex-wrap gap-3">
                    {s.chips.map((c) => (
                      <span
                        key={c}
                        className="inline-block border border-[#E4E5DF] bg-white text-xs tracking-wide px-4 py-2 text-[#1A1C1A]"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
